import type { FilesProxySchemaWithId } from '@iconik-io/iconik-api/src/services/files';

import { getStreamFormat, getStreamFormatByName } from './get_stream_format';
import { preferNonMultichannelProxy } from './prefer_non_multichannel_proxy';
import { sortByMediaTypeAndDate } from './sort_proxies_by_media_type';

/**
 * Whether a proxy is a stream rather than a progressive file. This answers what
 * the proxy is, not whether it can be played: search results carry the format
 * name but no URL, so the name is checked first and the URL shape is only a
 * fallback for proxies that arrive without one.
 */
function isStreamProxy(proxy: FilesProxySchemaWithId): boolean {
  if (proxy.format && getStreamFormatByName(proxy.format) !== undefined) {
    return true;
  }
  return proxy.url ? getStreamFormat(proxy.url) !== undefined : false;
}

export function findPlayableProxies(proxies: FilesProxySchemaWithId[]) {
  const playable = proxies.filter(
    (proxy) =>
      proxy.status === 'CLOSED' ||
      // A growing proxy is only worth playing once there is a manifest to load
      (proxy.status === 'GROWING' &&
        isStreamProxy(proxy) &&
        Boolean(proxy.url)),
  );
  playable.sort(sortByMediaTypeAndDate);
  return playable;
}

export function findLatestPlayableProxy(proxies: FilesProxySchemaWithId[]) {
  return preferNonMultichannelProxy(findPlayableProxies(proxies));
}

/**
 * Whether the asset is being written to right now, ie. it has a stream proxy
 * that is still growing. Deliberately independent of the URL, so it holds for
 * search results, where proxies arrive without one.
 */
export function hasGrowingStreamProxy(proxies: FilesProxySchemaWithId[]) {
  return proxies.some(
    (proxy) => proxy.status === 'GROWING' && isStreamProxy(proxy),
  );
}
